import React, { useState, useRef, useEffect, Suspense } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { MapPin, Clock, Phone, Plane, Warehouse, Globe2, ShieldCheck, ShieldAlert, Mail, Download, Package, Truck } from 'lucide-react';
import { warehouseData } from '../i18n/translations';
import { getCountryName } from '../lib/countries';

// TSA Bill of Lading template — served from frontend/public/docs
const BOL_TEMPLATE_URL = '/docs/MailAmericas-BOL-Warehouse-Drop-Off.pdf';

// Commercial carriers accepted at the carrier-only warehouses (ORD, EWR, LAX)
const US_CARRIERS = {
  parcel: ['UPS', 'FedEx', 'USPS', 'OnTrac', 'Amazon Shipping'],
  freight: ['TForce Freight', 'Old Dominion (ODFL)', 'Estes Express', 'XPO', 'Saia', 'R+L Carriers', 'ABF Freight']
};

// Lazy load Globe component
const Globe = React.lazy(() => import('react-globe.gl'));

// Destination data - key cities in each destination country
const destinationData = [
  // Americas (31 countries)
  { name: 'Buenos Aires', country: 'AR', lat: -34.6037, lng: -58.3816, region: 'america' },
  { name: 'São Paulo', country: 'BR', lat: -23.5505, lng: -46.6333, region: 'america' },
  { name: 'Ciudad de México', country: 'MX', lat: 19.4326, lng: -99.1332, region: 'america' },
  { name: 'Santiago', country: 'CL', lat: -33.4489, lng: -70.6693, region: 'america' },
  { name: 'Bogotá', country: 'CO', lat: 4.7110, lng: -74.0721, region: 'america' },
  { name: 'Lima', country: 'PE', lat: -12.0464, lng: -77.0428, region: 'america' },
  { name: 'Caracas', country: 'VE', lat: 10.4806, lng: -66.9036, region: 'america' },
  { name: 'Montevideo', country: 'UY', lat: -34.9011, lng: -56.1645, region: 'america' },
  { name: 'Panamá City', country: 'PA', lat: 8.9824, lng: -79.5199, region: 'america' },
  { name: 'San José', country: 'CR', lat: 9.9281, lng: -84.0907, region: 'america' },
  { name: 'Toronto', country: 'CA', lat: 43.6532, lng: -79.3832, region: 'america' },
  { name: 'Kingston', country: 'JM', lat: 17.9714, lng: -76.7931, region: 'america' },
  // Africa (44 countries)
  { name: 'Johannesburg', country: 'ZA', lat: -26.2041, lng: 28.0473, region: 'africa' },
  { name: 'Lagos', country: 'NG', lat: 6.5244, lng: 3.3792, region: 'africa' },
  { name: 'Cairo', country: 'EG', lat: 30.0444, lng: 31.2357, region: 'africa' },
  { name: 'Casablanca', country: 'MA', lat: 33.5731, lng: -7.5898, region: 'africa' },
  { name: 'Nairobi', country: 'KE', lat: -1.2921, lng: 36.8219, region: 'africa' },
  { name: 'Accra', country: 'GH', lat: 5.6037, lng: -0.1870, region: 'africa' },
  { name: 'Addis Ababa', country: 'ET', lat: 8.9806, lng: 38.7578, region: 'africa' },
  { name: 'Dakar', country: 'SN', lat: 14.7167, lng: -17.4677, region: 'africa' },
  { name: 'Tunis', country: 'TN', lat: 36.8065, lng: 10.1815, region: 'africa' },
  { name: 'Dar es Salaam', country: 'TZ', lat: -6.7924, lng: 39.2083, region: 'africa' },
  // Asia & Middle East (14 countries)  
  { name: 'Dubai', country: 'AE', lat: 25.2048, lng: 55.2708, region: 'asia' },
  { name: 'Singapore', country: 'SG', lat: 1.3521, lng: 103.8198, region: 'asia' },
  { name: 'Tel Aviv', country: 'IL', lat: 32.0853, lng: 34.7818, region: 'asia' },
  { name: 'Riyadh', country: 'SA', lat: 24.7136, lng: 46.6753, region: 'asia' },
  { name: 'Doha', country: 'QA', lat: 25.2854, lng: 51.5310, region: 'asia' },
  { name: 'Bangkok', country: 'TH', lat: 13.7563, lng: 100.5018, region: 'asia' },
  { name: 'Manila', country: 'PH', lat: 14.5995, lng: 120.9842, region: 'asia' },
  { name: 'Jakarta', country: 'ID', lat: -6.2088, lng: 106.8456, region: 'asia' },
  { name: 'Kuala Lumpur', country: 'MY', lat: 3.1390, lng: 101.6869, region: 'asia' },
  { name: 'Ho Chi Minh', country: 'VN', lat: 10.8231, lng: 106.6297, region: 'asia' },
];

// Full destination country lists by region (all 89 countries), as ISO codes.
// Names come from getCountryName so they follow the site language.
const destinationsByRegion = {
  america: [
    'AR', 'BR', 'MX', 'CL', 'CO', 'PE', 'VE', 'UY',
    'PY', 'BO', 'EC', 'PA', 'CR', 'GT', 'HN',
    'SV', 'NI', 'DO', 'CU', 'HT', 'JM',
    'TT', 'BS', 'BB', 'PR', 'BZ', 'GY',
    'SR', 'AG', 'LC', 'CA'
  ],
  africa: [
    'ZA', 'NG', 'EG', 'MA', 'KE', 'GH', 'ET', 'SN',
    'TN', 'TZ', 'DZ', 'AO', 'CM', 'CI', 'UG',
    'ZW', 'MZ', 'ZM', 'MG', 'ML', 'BF', 'NE',
    'RW', 'TD', 'GN', 'BJ', 'TG', 'SL', 'LR', 'MR',
    'BW', 'NA', 'GA', 'LS', 'SZ', 'MU', 'CV',
    'DJ', 'KM', 'ER', 'GM', 'SC', 'SD', 'LY'
  ],
  asia: [
    'AE', 'SG', 'IL', 'SA', 'QA', 'TH',
    'PH', 'ID', 'MY', 'VN', 'KW', 'OM', 'BH', 'JO'
  ]
};

// Miami hub coordinates
const MIAMI_HUB = { lat: 25.7617, lng: -80.1918 };

const NetworkSection = () => {
  const { language, t } = useLanguage();
  const [viewMode, setViewMode] = useState('origins'); // 'origins' or 'destinations'
  const [activeRegion, setActiveRegion] = useState('usa');
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const globeRef = useRef();
  const [globeReady, setGlobeReady] = useState(false);
  const [globeSize, setGlobeSize] = useState({ width: 600, height: 600 });

  const originRegions = ['usa', 'asia', 'europe', 'africa', 'southamerica'];
  const destinationRegions = ['america', 'africa', 'asia'];
  
  const filteredWarehouses = warehouseData.filter(w => w.region === activeRegion);
  const filteredDestinations = destinationData.filter(d => d.region === activeRegion);

  // Globe points based on view mode
  const getGlobePoints = () => {
    if (viewMode === 'origins') {
      return warehouseData.map(w => ({
        lat: w.lat,
        lng: w.lng,
        name: w.name,
        id: w.id,
        color: w.region === activeRegion ? '#7AC142' : '#444',
        size: w.region === activeRegion ? 0.5 : 0.2
      }));
    } else {
      return destinationData.map(d => ({
        lat: d.lat,
        lng: d.lng,
        name: d.name,
        country: getCountryName(d.country, language),
        color: d.region === activeRegion ? '#00BFFF' : '#444',
        size: d.region === activeRegion ? 0.4 : 0.15
      }));
    }
  };

  // Arcs from Miami to destinations
  const getArcs = () => {
    if (viewMode === 'destinations') {
      return filteredDestinations.map(d => ({
        startLat: MIAMI_HUB.lat,
        startLng: MIAMI_HUB.lng,
        endLat: d.lat,
        endLng: d.lng,
        color: ['#7AC142', '#00BFFF']
      }));
    }
    return [];
  };

  // Responsive globe size
  useEffect(() => {
    const updateSize = () => {
      const width = window.innerWidth;
      if (width < 768) {
        // Page and card padding take 64px: the globe must not widen the page
        const size = Math.min(350, width - 64);
        setGlobeSize({ width: size, height: size });
      } else if (width < 1024) {
        setGlobeSize({ width: 450, height: 450 });
      } else {
        setGlobeSize({ width: 550, height: 550 });
      }
    };
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  // Camera position based on active region
  useEffect(() => {
    if (globeRef.current && globeReady) {
      if (viewMode === 'origins') {
        const regionWarehouses = warehouseData.filter(w => w.region === activeRegion);
        if (regionWarehouses.length > 0) {
          const avgLat = regionWarehouses.reduce((sum, w) => sum + w.lat, 0) / regionWarehouses.length;
          const avgLng = regionWarehouses.reduce((sum, w) => sum + w.lng, 0) / regionWarehouses.length;
          globeRef.current.pointOfView({ lat: avgLat, lng: avgLng, altitude: 1.8 }, 1000);
        }
      } else {
        const regionDests = destinationData.filter(d => d.region === activeRegion);
        if (regionDests.length > 0) {
          const avgLat = regionDests.reduce((sum, d) => sum + d.lat, 0) / regionDests.length;
          const avgLng = regionDests.reduce((sum, d) => sum + d.lng, 0) / regionDests.length;
          globeRef.current.pointOfView({ lat: avgLat, lng: avgLng, altitude: 2.2 }, 1000);
        }
      }
    }
  }, [activeRegion, globeReady, viewMode]);

  // Reset active region when switching view mode
  useEffect(() => {
    if (viewMode === 'origins') {
      setActiveRegion('usa');
    } else {
      setActiveRegion('america');
    }
  }, [viewMode]);

  // Listen for navbar nav events to switch view mode
  useEffect(() => {
    const handler = (e) => {
      if (e.detail === 'origins' || e.detail === 'destinations') {
        setViewMode(e.detail);
      }
    };
    window.addEventListener('setNetworkView', handler);
    return () => window.removeEventListener('setNetworkView', handler);
  }, []);

  // Jump to a warehouse card when another section asks for it (e.g. the US ops table)
  const [highlightId, setHighlightId] = useState(null);
  useEffect(() => {
    let timers = [];
    const handler = (e) => {
      const target = warehouseData.find((w) => w.id === e.detail);
      if (!target) return;
      timers.forEach(clearTimeout);
      setViewMode('origins');
      setHighlightId(target.id);
      // Region is set after the viewMode effect, which resets it to 'usa'
      timers = [
        setTimeout(() => setActiveRegion(target.region), 0),
        setTimeout(() => document.getElementById(`warehouse-card-${target.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 150),
        setTimeout(() => setHighlightId(null), 3500)
      ];
    };
    window.addEventListener('focusWarehouse', handler);
    return () => { window.removeEventListener('focusWarehouse', handler); timers.forEach(clearTimeout); };
  }, []);

  const regions = viewMode === 'origins' ? originRegions : destinationRegions;

  return (
    <section
      id="warehouses"
      data-testid="network-section"
      className="py-24 md:py-32 bg-[#0a0a0a] relative overflow-hidden"
    >
      {/* Background glow effect */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#7AC142]/5 rounded-full blur-3xl pointer-events-none" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* View Mode Toggle */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex bg-black/50 border border-white/10 p-1 rounded-lg">
            <button
              onClick={() => setViewMode('origins')}
              className={`flex items-center gap-2 px-3 sm:px-6 py-3 text-xs sm:text-sm font-bold uppercase tracking-wider transition-all rounded-md ${
                viewMode === 'origins'
                  ? 'bg-[#7AC142] text-black'
                  : 'text-white/60 hover:text-white'
              }`}
              data-testid="view-origins-btn"
            >
              <Warehouse className="w-4 h-4" />
              {t('network.origins')}
            </button>
            <button
              onClick={() => setViewMode('destinations')}
              className={`flex items-center gap-2 px-3 sm:px-6 py-3 text-xs sm:text-sm font-bold uppercase tracking-wider transition-all rounded-md ${
                viewMode === 'destinations'
                  ? 'bg-[#00BFFF] text-black'
                  : 'text-white/60 hover:text-white'
              }`}
              data-testid="view-destinations-btn"
            >
              <Plane className="w-4 h-4" />
              {t('network.destinations')}
            </button>
          </div>
        </div>

        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
          key={viewMode}
        >
          <span className={`eyebrow mb-4 ${viewMode === 'origins' ? '' : 'eyebrow-info'}`}>
            {viewMode === 'origins' ? <Warehouse className="w-4 h-4" /> : <Plane className="w-4 h-4" />}
            {viewMode === 'origins' ? t('network.originsLabel') : t('network.destinationsLabel')}
          </span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white tracking-tight">
            {viewMode === 'origins' ? t('network.originsTitle') : t('network.destinationsTitle')}{' '}
            <span className={viewMode === 'origins' ? 'text-[#7AC142]' : 'text-[#00BFFF]'}>
              {viewMode === 'origins' ? t('network.originsHighlight') : t('network.destinationsHighlight')}
            </span>
          </h2>
          <p className="mt-4 text-gray-400 max-w-2xl mx-auto text-lg">
            {viewMode === 'origins' ? t('network.originsSubtitle') : t('network.destinationsSubtitle')}
          </p>
        </motion.div>

        {/* Main content grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          {/* Globe */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="relative order-2 lg:order-1 min-w-0"
          >
            <div className="relative bg-black/30 backdrop-blur-sm rounded-2xl border border-white/5 p-4 flex items-center justify-center">
              <Suspense fallback={
                <div className="flex items-center justify-center" style={{ width: globeSize.width, height: globeSize.height }}>
                  <div className="w-16 h-16 border-4 border-[#7AC142]/20 border-t-[#7AC142] rounded-full animate-spin" />
                </div>
              }>
                <Globe
                  ref={globeRef}
                  width={globeSize.width}
                  height={globeSize.height}
                  globeImageUrl="//unpkg.com/three-globe/example/img/earth-dark.jpg"
                  backgroundImageUrl="//unpkg.com/three-globe/example/img/night-sky.png"
                  pointsData={getGlobePoints()}
                  pointLat="lat"
                  pointLng="lng"
                  pointColor="color"
                  pointAltitude={0.01}
                  pointRadius="size"
                  pointLabel={d => `<div class="bg-black/80 text-white px-2 py-1 text-xs rounded">${d.name}${d.country ? `, ${d.country}` : ''}</div>`}
                  arcsData={getArcs()}
                  arcStartLat="startLat"
                  arcStartLng="startLng"
                  arcEndLat="endLat"
                  arcEndLng="endLng"
                  arcColor="color"
                  arcDashLength={0.5}
                  arcDashGap={0.2}
                  arcDashAnimateTime={2000}
                  arcStroke={0.5}
                  atmosphereColor={viewMode === 'origins' ? "#7AC142" : "#00BFFF"}
                  atmosphereAltitude={0.15}
                  onGlobeReady={() => setGlobeReady(true)}
                />
              </Suspense>

              {/* Stats overlay */}
              <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 flex gap-6 bg-black/80 backdrop-blur-sm px-6 py-3 border border-white/10">
                <div className="text-center">
                  <p className={`text-2xl font-bold font-mono ${viewMode === 'origins' ? 'text-[#7AC142]' : 'text-[#00BFFF]'}`}>
                    {viewMode === 'origins' ? warehouseData.length : '89'}
                  </p>
                  <p className="text-[10px] text-gray-500 uppercase tracking-wider">
                    {viewMode === 'origins' ? t('network.locations') : t('network.countries')}
                  </p>
                </div>
                <div className="w-px bg-white/10" />
                <div className="text-center">
                  <p className={`text-2xl font-bold font-mono ${viewMode === 'origins' ? 'text-[#7AC142]' : 'text-[#00BFFF]'}`}>
                    {viewMode === 'origins' ? '5' : '3'}
                  </p>
                  <p className="text-[10px] text-gray-500 uppercase tracking-wider">{t('network.continents')}</p>
                </div>
                {viewMode === 'origins' && (
                  <>
                    <div className="w-px bg-white/10" />
                    <div className="text-center">
                      <p className="text-2xl font-bold text-[#7AC142] font-mono">DG</p>
                      <p className="text-[10px] text-gray-500 uppercase tracking-wider">{t('hero.stats.certified')}</p>
                    </div>
                  </>
                )}
              </div>
            </div>
          </motion.div>

          {/* Region tabs and info */}
          <div className="order-1 lg:order-2 min-w-0">
            {/* Region tabs */}
            <div className="flex flex-wrap gap-2 mb-6">
              {regions.map((region) => (
                <button
                  key={region}
                  onClick={() => setActiveRegion(region)}
                  className={`px-4 py-2 text-sm font-bold uppercase tracking-wider transition-all border ${
                    activeRegion === region
                      ? viewMode === 'origins'
                        ? 'bg-[#7AC142] text-black border-[#7AC142]'
                        : 'bg-[#00BFFF] text-black border-[#00BFFF]'
                      : 'text-white/60 border-white/10 hover:border-white/30'
                  }`}
                  data-testid={`region-tab-${region}`}
                >
                  {t(`network.regions.${region}`)}
                </button>
              ))}
            </div>

            {/* Origins: Warehouse list */}
            {viewMode === 'origins' && (
              <motion.div
                key={activeRegion}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-4 max-h-[640px] overflow-y-auto pr-2"
              >
                {/* Security notice: carrier-only warehouses — shown only for USA region */}
                {activeRegion === 'usa' && (
                  <div
                    className="bg-gradient-to-br from-orange-500/10 to-transparent border border-orange-500/40 overflow-hidden"
                    data-testid="security-notice"
                  >
                    <div className="px-5 py-3 flex flex-wrap items-center justify-between gap-2 bg-orange-500/10 border-b border-orange-500/20">
                      <div className="flex items-center gap-2">
                        <ShieldAlert className="w-4 h-4 text-orange-400" />
                        <span className="text-[10px] font-mono font-bold uppercase tracking-[0.18em] text-orange-400">
                          {t('network.securityNotice.badge')}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        {['ORD', 'EWR', 'LAX'].map((hub) => (
                          <span key={hub} className="text-[10px] font-mono font-bold tracking-widest px-2 py-0.5 bg-orange-500/10 text-orange-400 border border-orange-500/30">
                            {hub}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="px-5 py-4">
                      <h5 className="text-base font-bold text-white leading-snug">{t('network.securityNotice.title')}</h5>
                      <p className="text-xs text-gray-400 mt-2 leading-relaxed">{t('network.securityNotice.body')}</p>
                      <p className="text-xs text-gray-300 mt-2 leading-relaxed">{t('network.securityNotice.howTo')}</p>
                      <div className="mt-4 grid sm:grid-cols-2 gap-4">
                        {[
                          { key: 'parcel', label: t('network.securityNotice.parcelLabel'), Icon: Package },
                          { key: 'freight', label: t('network.securityNotice.freightLabel'), Icon: Truck }
                        ].map(({ key, label, Icon }) => (
                          <div key={key}>
                            <p className="flex items-center gap-1.5 text-[10px] font-mono font-bold uppercase tracking-[0.14em] text-gray-500 mb-2">
                              <Icon className="w-3.5 h-3.5 text-orange-400" />
                              {label}
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                              {US_CARRIERS[key].map((carrier) => (
                                <span key={carrier} className="px-2.5 py-1 bg-white/[0.04] border border-white/10 text-xs text-gray-200">
                                  {carrier}
                                </span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                      <p className="mt-3 text-xs text-gray-400 leading-relaxed">
                        {t('network.securityNotice.carriersNote')}
                      </p>
                      <p className="mt-4 pt-3 border-t border-white/5 text-[11px] text-gray-500 leading-relaxed">
                        {t('network.securityNotice.footnote')}
                      </p>
                    </div>
                  </div>
                )}

                {/* Universal SOP banner — shown only for USA region */}
                {activeRegion === 'usa' && (
                  <div className="bg-gradient-to-br from-[#7AC142]/8 to-transparent border border-[#7AC142]/25 overflow-hidden">
                    <div className="px-5 py-3 flex items-center justify-between bg-[#7AC142]/5 border-b border-[#7AC142]/15">
                      <div className="flex items-center gap-2">
                        <ShieldCheck className="w-4 h-4 text-[#7AC142]" />
                        <span className="text-[10px] font-mono font-bold uppercase tracking-[0.18em] text-[#7AC142]">
                          {t('network.sopBadge')}
                        </span>
                      </div>
                    </div>
                    <div className="px-5 py-4">
                      <h5 className="text-base font-bold text-white">{t('network.sopTitle')}</h5>
                      <p className="text-xs text-gray-400 mt-1 leading-relaxed">{t('network.sopSubtitle')}</p>
                      <ol className="mt-4 space-y-3">
                        {(Array.isArray(t('network.sopRules')) ? t('network.sopRules') : []).map((rule, i) => (
                          <li key={i} className="flex gap-3">
                            <span className="font-mono font-bold text-[#7AC142] text-xs shrink-0 w-6 pt-0.5">
                              {String(i + 1).padStart(2, '0')}
                            </span>
                            <div className="flex-1">
                              <p className="text-sm font-bold text-white leading-tight">{rule.t}</p>
                              <p className="text-xs text-gray-400 mt-1 leading-relaxed">{rule.d}</p>
                              {rule.download && (
                                <a
                                  href={BOL_TEMPLATE_URL}
                                  download
                                  className="mt-2 inline-flex items-center gap-2 px-3 py-2 bg-[#7AC142] text-black text-[11px] font-bold uppercase tracking-wider hover:bg-[#69A835] transition-colors"
                                  data-testid="sop-bol-download"
                                >
                                  <Download className="w-3.5 h-3.5" />
                                  {t('network.sopDocLabel')}
                                </a>
                              )}
                            </div>
                          </li>
                        ))}
                      </ol>
                      <div className="mt-4 pt-3 border-t border-white/5 flex flex-wrap items-center gap-2 text-xs">
                        <Mail className="w-3.5 h-3.5 text-[#7AC142]" />
                        <span className="text-gray-500">{t('network.sopContactLabel')}</span>
                        <a href="mailto:warehouse_group@mailamericas.com" className="text-[#7AC142] hover:underline font-mono break-all">
                          warehouse_group@mailamericas.com
                        </a>
                      </div>
                    </div>
                  </div>
                )}

                {filteredWarehouses.map((warehouse) => (
                  <div
                    key={warehouse.id}
                    id={`warehouse-card-${warehouse.id}`}
                    className={`bg-white/[0.03] border transition-all duration-500 overflow-hidden ${
                      highlightId === warehouse.id
                        ? 'border-[#7AC142] ring-2 ring-[#7AC142]/60 shadow-[0_0_24px_rgba(122,193,66,0.35)]'
                        : 'border-white/10 hover:border-[#7AC142]/30'
                    }`}
                  >
                    {/* Header */}
                    <div className="px-5 py-4 bg-gradient-to-r from-[#7AC142]/10 to-transparent border-b border-white/5 flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 rounded-full bg-[#7AC142] shadow-[0_0_8px_#7AC142]" />
                        <h4 className="font-bold text-white tracking-wide">{warehouse.name}</h4>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        {warehouse.scopeTag && (
                          <span className="text-[10px] font-mono font-bold tracking-widest px-2 py-1 bg-sky-500/10 text-sky-400 border border-sky-500/40">
                            {warehouse.scopeTag[language] || warehouse.scopeTag.en}
                          </span>
                        )}
                        {warehouse.carrierOnly && (
                          <span
                            className="text-[10px] font-mono font-bold tracking-widest px-2 py-1 bg-orange-500/10 text-orange-400 border border-orange-500/40"
                            title={t('network.securityNotice.title')}
                          >
                            {t('network.securityNotice.tag')}
                          </span>
                        )}
                        {warehouse.dgAuthorized && (
                          <span
                            className="text-[10px] font-mono font-bold tracking-widest px-2 py-1 bg-yellow-500/10 text-yellow-500 border border-yellow-500/40"
                            title={t('dangerousGoods.miamiOnly.locationLabel')}
                          >
                            {t('network.dgAuthorized')}
                          </span>
                        )}
                        {warehouse.hub && (
                          <span className="text-[10px] font-mono font-bold tracking-widest px-2 py-1 bg-[#7AC142]/10 text-[#7AC142] border border-[#7AC142]/30">
                            {warehouse.hub}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Meta */}
                    <div className="px-5 py-4 space-y-2.5">
                      <div className="flex items-start gap-2.5 text-sm text-gray-300">
                        <MapPin className="w-4 h-4 text-[#7AC142] shrink-0 mt-0.5" />
                        <span>{warehouse.address}</span>
                      </div>
                      {warehouse.phone && (
                        <div className="flex items-center gap-2.5 text-sm text-gray-300">
                          <Phone className="w-4 h-4 text-[#7AC142]" />
                          <span>{warehouse.phone}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2.5 text-sm text-gray-300">
                        <Clock className="w-4 h-4 text-[#7AC142]" />
                        <span>{warehouse.hours[language] || warehouse.hours.en}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </motion.div>
            )}

            {/* Destinations: Country list */}
            {viewMode === 'destinations' && (
              <motion.div
                key={activeRegion}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-4"
              >
                <div className="bg-white/5 border border-white/10 p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-bold text-white flex items-center gap-2">
                      <Plane className="w-5 h-5 text-[#00BFFF]" />
                      {t(`network.regions.${activeRegion}`)}
                    </h4>
                    <span className="text-[#00BFFF] font-mono font-bold">
                      {destinationsByRegion[activeRegion]?.length || 0} {t('network.countries')}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2 max-h-[420px] overflow-y-auto pr-1">
                    {destinationsByRegion[activeRegion]?.map((code) => (
                      <span
                        key={code}
                        className="px-3 py-1.5 bg-[#00BFFF]/10 border border-[#00BFFF]/20 text-sm text-gray-300"
                        data-testid={`destination-${code}`}
                      >
                        {getCountryName(code, language)}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default NetworkSection;
